import { useEffect, useState } from 'react';
import { Bell, X, Loader2 } from 'lucide-react';
import { getPushStatus, enablePush } from '../lib/push';
import { toast } from 'sonner';

const ASKED_KEY = 'gd_notify_asked';

function isNative() {
    return typeof window !== 'undefined' && !!(window.Capacitor?.isNativePlatform?.());
}

/**
 * Gentle soft-ask for notification permission shown once after entering the app.
 * Makes clear it's optional and can be changed later under Profile.
 */
export default function NotificationGate() {
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const t = setTimeout(async () => {
            try {
                if (localStorage.getItem(ASKED_KEY)) return;
                if (!localStorage.getItem('gd_tutorial_done')) return; // let the tutorial finish first
            } catch { return; }

            if (isNative()) {
                if (!cancelled) setShow(true);
                return;
            }
            try {
                const s = await getPushStatus();
                // Only soft-ask when supported and the user hasn't decided yet
                if (s.supported && !s.granted && !s.subscribed && !s.denied) {
                    if (!cancelled) setShow(true);
                } else {
                    localStorage.setItem(ASKED_KEY, '1');
                }
            } catch { /* ignore */ }
        }, 3500);
        return () => { cancelled = true; clearTimeout(t); };
    }, []);

    const dismiss = () => {
        try { localStorage.setItem(ASKED_KEY, '1'); } catch { /* ignore */ }
        setShow(false);
    };

    const enable = async () => {
        setBusy(true);
        try {
            if (isNative()) {
                const { ensureNativePush } = await import('../lib/nativePush');
                const res = await ensureNativePush();
                if (res?.error) toast.error('You can enable notifications later under Profile.');
                else toast.success('Notifications enabled 🌱');
            } else {
                await enablePush();
                toast.success('Notifications enabled 🌱');
            }
        } catch {
            toast.error('You can enable notifications later under Profile.');
        } finally {
            setBusy(false);
            dismiss();
        }
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 z-[85] bg-brand-ink/45 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={dismiss} data-testid="notify-gate">
            <div className="relative bg-brand-bg w-full sm:max-w-sm rounded-t-3xl sm:rounded-3xl border border-brand-border p-6 text-center animate-fade-up" onClick={(e) => e.stopPropagation()}>
                <button data-testid="notify-gate-close" onClick={dismiss} className="absolute top-4 right-4 p-1 text-brand-ink3 hover:text-brand-ink"><X size={18} /></button>
                <div className="w-14 h-14 rounded-2xl bg-brand-green/12 text-brand-green flex items-center justify-center mx-auto mb-4">
                    <Bell size={26} strokeWidth={1.7} />
                </div>
                <h3 className="font-serif text-xl text-brand-ink">Stay on top of your garden</h3>
                <p className="text-sm text-brand-ink2 mt-2 leading-relaxed">
                    Turn on notifications for watering reminders and frost alerts. It's completely optional —
                    you can enable or turn it off anytime under <strong>Profile</strong>.
                </p>
                <button data-testid="notify-gate-enable" onClick={enable} disabled={busy}
                    className="w-full mt-5 inline-flex items-center justify-center gap-2 bg-brand-green text-white py-3 rounded-full text-sm font-medium hover:bg-brand-greenDark disabled:opacity-50 transition-colors">
                    {busy ? <Loader2 size={15} className="animate-spin" /> : <Bell size={15} />}
                    {busy ? 'Enabling…' : 'Enable notifications'}
                </button>
                <button data-testid="notify-gate-later" onClick={dismiss} className="w-full mt-2 text-sm text-brand-ink3 hover:text-brand-ink py-2">
                    Maybe later
                </button>
            </div>
        </div>
    );
}
